import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Download, X, Smartphone } from 'lucide-react';
import { checkInstallable, promptInstall, isPWA } from '@/serviceWorkerRegistration';

const PWAInstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already running as PWA
    if (isPWA()) {
      setIsInstalled(true);
      return;
    }

    // Check if install prompt is available
    checkInstallable((canInstall, prompt) => {
      if (canInstall) {
        setDeferredPrompt(prompt);
        // Show prompt after a delay (not immediately)
        const timer = setTimeout(() => {
          // Only show if user hasn't dismissed before
          const dismissed = localStorage.getItem('pwa-prompt-dismissed');
          if (!dismissed) {
            setShowPrompt(true);
          }
        }, 30000); // 30 seconds
        return () => clearTimeout(timer);
      }
    });
  }, []);

  const handleInstall = async () => {
    const installed = await promptInstall(deferredPrompt);
    if (installed) {
      setIsInstalled(true);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', 'true');
  };

  // Don't show if already installed or no prompt available
  if (isInstalled || !showPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-slide-up">
      <Card className="p-4 bg-gradient-to-r from-primary to-indigo-700 text-white shadow-xl border-0">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-white/20 rounded-lg">
            <Smartphone size={24} />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-sm mb-1">Install Treppan CRM</h4>
            <p className="text-xs text-white/80 mb-3">
              Install our app for a better experience with offline access and quick launch from your home screen.
            </p>
            <div className="flex gap-2">
              <Button 
                size="sm" 
                className="bg-white text-primary hover:bg-white/90"
                onClick={handleInstall}
                data-testid="pwa-install-btn"
              >
                <Download size={14} className="mr-1" />
                Install
              </Button>
              <Button 
                size="sm" 
                variant="ghost" 
                className="text-white hover:bg-white/20"
                onClick={handleDismiss}
                data-testid="pwa-dismiss-btn"
              >
                Not now
              </Button>
            </div>
          </div>
          <button 
            onClick={handleDismiss}
            className="text-white/60 hover:text-white"
          >
            <X size={18} />
          </button>
        </div>
      </Card>
    </div>
  );
};

export default PWAInstallPrompt;
